import { useMemo, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function AddTeamDialog({
    tournamentId,
    categoria,
    matches = [],
    open,
    onOpenChange,
    onSaved,
}) {
    const isGroups = categoria?.formato === "grupos";
    const [nombre, setNombre] = useState("");
    const [institucion, setInstitucion] = useState("");
    const [grupo, setGrupo] = useState(""); // "" = auto (grupo más pequeño)
    const [saving, setSaving] = useState(false);
    const [logoUrl, setLogoUrl] = useState("");
    const [uploadingLogo, setUploadingLogo] = useState(false);

    const uploadLogo = async (file) => {
        if (!file) return;
        setUploadingLogo(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/uploads/image", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setLogoUrl(data.url);
            toast.success("Escudo cargado");
        } catch (err) {
            toast.error(
                formatApiError(err.response?.data?.detail) ||
                    "No se pudo subir el escudo",
            );
        } finally {
            setUploadingLogo(false);
        }
    };

    // Descubre los grupos existentes desde los matches de la categoría
    const groupInfo = useMemo(() => {
        if (!isGroups) return { grupos: [], counts: {} };
        const teamGrupo = {};
        for (const m of matches) {
            if (m.category_id !== categoria.id) continue;
            const g = m.grupo || "A";
            if (g === "Único" || g === "Playoffs") continue;
            for (const nm of [m.local, m.visitante]) {
                if (nm && !teamGrupo[nm]) teamGrupo[nm] = g;
            }
        }
        const counts = {};
        Object.values(teamGrupo).forEach((g) => {
            counts[g] = (counts[g] || 0) + 1;
        });
        const grupos = Object.keys(counts).sort();
        return { grupos, counts };
    }, [matches, categoria, isGroups]);

    const submit = async (e) => {
        e.preventDefault();
        if (!nombre.trim()) return toast.error("Pon el nombre del equipo");
        setSaving(true);
        try {
            const payload = {
                nombre: nombre.trim(),
                institucion: institucion.trim(),
            };
            if (isGroups && grupo) payload.grupo = grupo;
            if (logoUrl) payload.logo_url = logoUrl;
            const { data } = await api.post(
                `/tournaments/${tournamentId}/categories/${categoria.id}/teams`,
                payload,
            );
            const grupoInfo = data.grupo ? ` en Grupo ${data.grupo}` : "";
            toast.success(
                `Equipo agregado${grupoInfo} · ${data.matches_created} partidos nuevos en bolsa`,
            );
            setNombre("");
            setInstitucion("");
            setGrupo("");
            setLogoUrl("");
            onSaved?.();
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md rounded-2xl">
                <DialogHeader>
                    <DialogTitle className="font-display">
                        Agregar equipo
                    </DialogTitle>
                    <DialogDescription>
                        Se generarán sus partidos {isGroups ? "contra los rivales de su grupo" : "contra todos los equipos existentes"} en{" "}
                        <strong>
                            {categoria?.rama} · {categoria?.nombre}
                        </strong>
                        , como borradores.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4 mt-2">
                    <div className="space-y-2">
                        <Label>Nombre del equipo</Label>
                        <Input
                            value={nombre}
                            onChange={(e) => setNombre(e.target.value)}
                            placeholder="Tigres B"
                            className="h-11 rounded-xl"
                            data-testid="add-team-name"
                            autoFocus
                        />
                    </div>
                    <div className="space-y-2">
                        <Label>Institución (opcional)</Label>
                        <Input
                            value={institucion}
                            onChange={(e) => setInstitucion(e.target.value)}
                            placeholder="CCH Sur"
                            className="h-11 rounded-xl"
                            data-testid="add-team-inst"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label>Escudo del equipo (opcional)</Label>
                        <div className="flex items-center gap-3">
                            <div className="w-14 h-14 rounded-xl border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden bg-slate-50">
                                {logoUrl ? (
                                    <img
                                        src={logoUrl}
                                        alt="escudo"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <span className="text-[10px] text-slate-400">
                                        sin escudo
                                    </span>
                                )}
                            </div>
                            <div className="flex-1 space-y-1">
                                <input
                                    id="add-team-logo-input"
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp,image/gif"
                                    className="hidden"
                                    data-testid="add-team-logo-input"
                                    onChange={(e) =>
                                        uploadLogo(e.target.files?.[0])
                                    }
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={uploadingLogo}
                                    onClick={() =>
                                        document
                                            .getElementById(
                                                "add-team-logo-input",
                                            )
                                            ?.click()
                                    }
                                    className="h-9 rounded-xl text-xs"
                                    data-testid="add-team-logo-upload"
                                >
                                    {uploadingLogo
                                        ? "Subiendo…"
                                        : logoUrl
                                          ? "Cambiar escudo"
                                          : "Subir escudo"}
                                </Button>
                                {logoUrl && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setLogoUrl("")}
                                        className="h-7 rounded-xl text-xs text-rose-600 hover:text-rose-800"
                                        data-testid="add-team-logo-clear"
                                    >
                                        Quitar
                                    </Button>
                                )}
                                <p className="text-[10px] text-slate-400">
                                    PNG/JPG/WEBP · Máx 2 MB
                                </p>
                            </div>
                        </div>
                    </div>
                    {isGroups && (
                        <div className="space-y-2">
                            <Label>Grupo</Label>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={() => setGrupo("")}
                                    className={`px-3 h-9 rounded-full text-xs font-bold border-2 transition-colors ${
                                        grupo === ""
                                            ? "bg-ipn-800 border-ipn-800 text-white"
                                            : "bg-white border-slate-200 text-slate-700 hover:border-ipn-300"
                                    }`}
                                    data-testid="add-team-group-auto"
                                >
                                    Auto (más pequeño)
                                </button>
                                {groupInfo.grupos.map((g) => (
                                    <button
                                        key={g}
                                        type="button"
                                        onClick={() => setGrupo(g)}
                                        className={`px-3 h-9 rounded-full text-xs font-bold border-2 transition-colors ${
                                            grupo === g
                                                ? "bg-ipn-800 border-ipn-800 text-white"
                                                : "bg-white border-slate-200 text-slate-700 hover:border-ipn-300"
                                        }`}
                                        data-testid={`add-team-group-${g}`}
                                    >
                                        Grupo {g}
                                        <span className="opacity-60 ml-1">
                                            ({groupInfo.counts[g]})
                                        </span>
                                    </button>
                                ))}
                            </div>
                            <p className="text-[10px] text-slate-500">
                                Solo generará partidos contra los equipos del
                                grupo elegido.
                            </p>
                        </div>
                    )}
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            className="rounded-xl"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            disabled={saving}
                            className="rounded-xl bg-ipn-800 hover:bg-ipn-900"
                            data-testid="add-team-submit"
                        >
                            {saving ? "Agregando…" : "Agregar"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
